<script setup lang="ts">
import { onMounted } from 'vue';
import { Zap } from 'lucide-vue-next';
import { storeOrdersApiClient } from "@api-client/store_orders";
import { Button } from "@/components/ui/button";

const isDev = import.meta.env.DEV;
const props = defineProps<{
    storeOrderId: string;
}>();
onMounted(() => {
    console.log("DebugOrderActions, props.storeOrderId", props.storeOrderId);
});

const processPartnerBalancesForOrder = async () => {
    const response = await (await storeOrdersApiClient.dev_test.process_partner_balances_for_order.$post({
        json: { storeOrderId: props.storeOrderId },
    })).json();
    console.log("DebugOrderActions, processPartnerBalancesForOrder response", response);
};

const runCron = async (action: 'cancelOldOrders_AbandonedByStore_CronHandler' | 'autoTopUpStoreBalance_CronHandler' | 'checkAndCompleteOrders_After24Hours_CronHandler_Store' | 'checkAndPlaceScheduledOrders_CronHandler_Store' | 'sendReminder_ForNotFinished_OnlinePayments_CronHandler_Store' | 'expireOldPayments_CronHandler_Store' | 'processPartnerBalances_CronHandler_Store' | 'checkAndCaptureStripeOrders_After4Hours_CronHandler_Store'
) => {
    const response = await (await storeOrdersApiClient.dev_test.run_cron.$post({
        json: { action },
    })).json();
    console.log("DebugOrderActions, response", response);
};

const updateTs = async (
    column: 'draft_create_at_ts' | 'order_placed_at_ts' | 'scheduled_at_ts',
    addMinutes: number
) => {
    const response = await (await storeOrdersApiClient.dev_test.update_ts.$post({
        json: {
            storeOrderId: props.storeOrderId,
            ts: Date.now() + (addMinutes * 60 * 1000),
            column
        },
    })).json();
    console.log("DebugOrderActions, response", response);
};

const runClass = async (action:
    'StoreAcceptOrderClass' |
    'StoreCancelOrderClass' |
    'StoreCompleteOrderClass' |
    'StripeVerifyStoreOrderClass' |
    'StorePlaceScheduledOrderClass' |
    'PaypalVerifyStoreOrderClass' |
    'StoreExpireOrderClass' |
    'StripeCaptureStoreOrderClass'
) => {
    const response = await (await storeOrdersApiClient.dev_test.run_class.$post({
        json: { storeOrderId: props.storeOrderId, action },
    })).json();
    console.log("DebugOrderActions, response", response.data);
};
const times = [(-60 * 24 * 3), (-60 * 24), -245, -65, -60, -25, -15, -10, -5, 0]
</script>

<template>
    <details v-if="isDev">
        <summary>
            <Zap class="h-5 w-5 text-yellow-500 inline-block cursor-pointer" />
            Debug Actions
        </summary>
        <div class="space-y-6 p-4 border rounded-lg">
            <div>
                <div class="text-xs">draft_create_at_ts</div>
                <span @click="updateTs('draft_create_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>
            <div>
                <div class="text-xs">order_placed_at_ts</div>
                <span @click="updateTs('order_placed_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>
            <div>
                <div class="text-xs">scheduled_at_ts</div>
                <span @click="updateTs('scheduled_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>

            <div class="flex flex-col gap-2 mt-4 border-t pt-4">
                <h3 class="text-lg font-bold">Class Actions</h3>
            </div>
            <div class="">
                <Button @click="runClass('StoreAcceptOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StoreAcceptOrderClass
                </Button>
                <Button @click="runClass('StoreCancelOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StoreCancelOrderClass
                </Button>
                <Button @click="runClass('StoreCompleteOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StoreCompleteOrderClass
                </Button>
                <Button @click="runClass('StripeVerifyStoreOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StripeVerifyStoreOrderClass
                </Button>
                <Button @click="runClass('PaypalVerifyStoreOrderClass')"
                        variant="secondary"
                        class="m-1">
                    PaypalVerifyStoreOrderClass
                </Button>
                <Button @click="runClass('StorePlaceScheduledOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StorePlaceScheduledOrderClass
                </Button>
                <Button @click="runClass('StoreExpireOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StoreExpireOrderClass
                </Button>
                <Button @click="runClass('StripeCaptureStoreOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StripeCaptureStoreOrderClass
                </Button>
                <Button @click="processPartnerBalancesForOrder()"
                        variant="secondary"
                        class="m-1">
                    Process Partner Balances (skip 14d wait)
                </Button>
            </div>


            <div class="flex flex-col gap-2 mt-4 border-t pt-4">
                <h3 class="text-lg font-bold">Cron Actions</h3>
            </div>
            <div>
                <Button @click="runCron('cancelOldOrders_AbandonedByStore_CronHandler')"
                        variant="secondary"
                        class="m-1">
                    Cancel Old Orders
                </Button>
                <Button @click="runCron('autoTopUpStoreBalance_CronHandler')"
                        variant="secondary"
                        class="m-1">
                    Auto Top-up Store Balance
                </Button>
                <Button @click="runCron('checkAndCompleteOrders_After24Hours_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    Complete Orders
                </Button>
                <Button @click="runCron('checkAndPlaceScheduledOrders_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    Place Scheduled Orders
                </Button>
                <Button @click="runCron('sendReminder_ForNotFinished_OnlinePayments_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    Send Reminder
                </Button>
                <Button @click="runCron('expireOldPayments_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    Expire Old Payments
                </Button>
                <Button @click="runCron('processPartnerBalances_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    processPartnerBalances_CronHandler_Store
                </Button>
                <Button @click="runCron('checkAndCaptureStripeOrders_After4Hours_CronHandler_Store')"
                        variant="secondary"
                        class="m-1">
                    Capture Stripe Orders
                </Button>
            </div>

        </div>
    </details>
</template>